import { useState } from "react";
import { TrendingUp, TrendingDown, MoreVertical } from "lucide-react";
import { formatMultiplier } from "../utils/upDownFormatters";
import { CUSTOM_EXPIRY_ID } from "../constants/upDownConstants";

export function UpDownExecutionDock({
  layoutVariant,
  activeLayout,
  showJackpot,
  luckyData,
  wallet,
  timePickerTriggerRef,
  isTimePickerOpen,
  setIsTimePickerOpen,
  openTimePicker,
  timerInput,
  dynamicExpiryOptions = [],
  selectedExpiryId,
  handleVariantChange,
  setTimerInput,
  amountPickerTriggerRef,
  isAmountPickerOpen,
  setIsAmountPickerOpen,
  openAmountPicker,
  amountInput,
  setAmountInput,
  amount,
  dynamicAmountOptions = [],
  handlePlaceOrder,
  isTradeActionDisabled,
  symbolActiveOrdersCount,
  playSound,
}) {
  const isMultiLayout = layoutVariant || (activeLayout && activeLayout !== "single");
  const [buttonShockwave, setButtonShockwave] = useState(null);

  return (
    <section
      className={`control-section overflow-scroll border border-white/10 ${
        isMultiLayout ? "mt-1 rounded-lg p-0" : " rounded-2xl p-0 md:p-4"
      }`}
    >
      {showJackpot && (
        <div
          className={`jackpot-anim show mb-2 mt-1 ${
            luckyData?.multiplier > 1 && showJackpot ? "show" : ""
          } rounded-lg border border-white/10 bg-[#1e2329] text-xs font-semibold text-white/80 md:text-sm shadow-inner ${
            isMultiLayout
              ? "mt-1 flex justify-between items-center gap-1 md:rounded-lg p-2 md:p-3"
              : "flex items-center justify-between overflow-hidden whitespace-nowrap gap-2 p-2 px-3 block"
          } `}
        >
          <div className="min-w-0 text-xs md:text-lg jackpot">
            Lucky Round:{" "}
            {(() => {
              const val = Number(
                luckyData?.multiplier || wallet?.binary?.activeMultiplier || 0,
              );
              if (val > 1) {
                return (
                  <span className="lucky-multiplier-active">
                    {formatMultiplier(val)}
                  </span>
                );
              } else {
                return <span className="lucky-multiplier-inactive">-</span>;
              }
            })()}
          </div>
          <div
            className={`min-w-0 text-xs md:text-lg jackpot ${
              isMultiLayout ? "text-left" : "text-left md:text-right"
            } `}
          >
            Jackpot:{" "}
            <span className="bg-[linear-gradient(180deg,#FFE600_26.32%,#FFF9BE_78.95%)] bg-clip-text text-transparent text-sm font-bold gradient-text">
              $
              {Number(
                wallet?.spin_and_win?.pool_amount || 0,
              ).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <div className={isMultiLayout ? "" : "md:flex md:gap-3 md:items-stretch"}>
        {/* Time settings container */}
        <div
          className={`grid rounded-xl border border-white/10 bg-[#2c2f36] ${
            isMultiLayout
              ? "mt-1 md:flex-1 p-1.5 md:p-3 md:block grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-2 md:gap-2 md:block"
              : "mt-1 md:mt-2 md:flex-1 p-1.5 md:p-3 md:block grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-2 md:gap-2 md:block"
          } `}
        >
          {!wallet || !wallet.binary ? (
            <div className="col-span-full flex items-center justify-center py-4">
              <svg
                className="w-5 h-5 text-[#ffd43b] animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            </div>
          ) : (
            <>
              <div
                className={`min-w-0 basis-[60%] flex items-center gap-2 md:block`}
              >
                <label
                  className={`font-semibold text-white/80 ${
                    isMultiLayout
                      ? "block mb-1 text-[11px]"
                      : "text-xs md:block md:mb-2 md:text-sm"
                  } `}
                >
                  Time
                </label>
                <button
                  ref={timePickerTriggerRef}
                  type="button"
                  aria-label="Trade time"
                  aria-haspopup="dialog"
                  aria-expanded={isTimePickerOpen}
                  className={`flex items-center rounded-lg text-left font-bold tracking-normal text-white bg-[#1a1c20] border border-white/10 outline-none transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#fc4420]/70 active:scale-[0.99] updown-input-nw updown-input-dragfield ${
                    isMultiLayout
                      ? "w-full h-[35px] px-3 text-lg mb-1"
                      : "flex-1 min-w-0 h-10 updown-input-nw px-3 text-xl mb-1 md:flex-none md:w-full md:h-[45px] md:px-4 md:text-2xl"
                  } `}
                  onClick={() => {
                    if (typeof playSound === "function") playSound("button");
                    if (isTimePickerOpen) {
                      setIsTimePickerOpen(false);
                    } else {
                      openTimePicker();
                    }
                  }}
                >
                  <span className="updown-input-value flex-1 min-w-0 text-black">
                    {timerInput}
                  </span>
                  <MoreVertical
                    size={20}
                    className="text-black opacity-50 ml-auto shrink-0"
                  />
                </button>
              </div>

              <div
                className={`grid shrink-0 grid-cols-2 ${
                  isMultiLayout
                    ? "basis-[40%] gap-1"
                    : "basis-[40%] gap-1.5 md:mt-2 md:flex md:w-full md:basis-auto md:gap-2"
                } `}
              >
                {dynamicExpiryOptions.slice(0, 4).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="tab"
                    aria-selected={
                      selectedExpiryId !== CUSTOM_EXPIRY_ID &&
                      selectedExpiryId === option.id
                    }
                    className={`${
                      isMultiLayout
                        ? "aspect-square h-6 min-w-0 w-full rounded-md px-1 text-[11px]"
                        : "h-6 min-w-0 rounded-md px-1 text-xs md:h-11 md:w-full md:text-sm"
                    } border font-bold transition hover:brightness-110 ${
                      selectedExpiryId !== CUSTOM_EXPIRY_ID &&
                      selectedExpiryId === option.id
                        ? "border-[#fc4420] bg-[#fc4420] text-white"
                        : "border-white/20 bg-[#3a3f45] text-white/80 hover:bg-[#454b52]"
                    } `}
                    onClick={() => {
                      if (typeof playSound === "function") playSound("button");
                      handleVariantChange(option.id);
                      setTimerInput(option.value);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Amount settings container */}
        <div
          className={`rounded-xl border border-white/10 bg-[#2c2f36] ${
            isMultiLayout
              ? "mt-2 rounded-lg p-1.5"
              : "mt-1 md:mt-2 md:flex-1 md:p-3"
          } `}
        >
          {!wallet || !wallet.binary ? (
            <div className="flex items-center justify-center py-4">
              <svg
                className="w-5 h-5 text-[#ffd43b] animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            </div>
          ) : (
            <>
              <div
                className={`items-center justify-between gap-2 hidden md:flex mb-0 `}
              >
                <label
                  htmlFor="updown-amount-input"
                  className={`font-semibold text-white/80 ${
                    isMultiLayout ? "text-[11px]" : "text-xs md:text-sm"
                  } `}
                >
                  Amount
                </label>
                <div
                  className={`text-right font-semibold md:flex hidden text-white/55 ${
                    isMultiLayout
                      ? "text-[10px] leading-[14px]"
                      : "text-[11px] leading-4 md:text-xs md:leading-5"
                  } `}
                >
                  <span>
                    Min $
                    {Number(wallet?.binary?.min_amount || 0).toFixed(2)}
                  </span>
                  <span className="ml-3">
                    Max $
                    {Number(wallet?.binary?.max_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div
                className={`grid ${
                  isMultiLayout
                    ? "grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1.6fr)] items-center gap-1.5 md:block mt-1 md:gap-2"
                    : "grid-cols-[auto_minmax(0,1.4fr)_auto_minmax(0,1.7fr)] items-center gap-1 md:gap-1.5 md:block md:gap-2"
                } `}
              >
                <label
                  htmlFor="updown-amount-input"
                  className="md:hidden text-xs font-semibold text-white/80 shrink-0"
                >
                  Amount
                </label>
                <button
                  id="updown-amount-input"
                  ref={amountPickerTriggerRef}
                  type="button"
                  aria-label="Trade amount"
                  aria-haspopup="dialog"
                  aria-expanded={isAmountPickerOpen}
                  className={`relative flex w-full items-center rounded-lg bg-[#1a1c20] text-white border border-white/10 text-left outline-none transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#fc4420]/70 active:scale-[0.99] updown-input-nw updown-input-dragfield updown-input-dragfield--amount ${
                    isMultiLayout
                      ? "h-[45px] px-3 mb-2 md:h-[35px] md:px-4 md:mb-3"
                      : "h-[45px] px-3 mb-2 md:h-11 md:px-4 md:mb-3"
                  } `}
                  onClick={() => {
                    if (typeof playSound === "function") playSound("button");
                    if (isAmountPickerOpen) {
                      setIsAmountPickerOpen(false);
                    } else {
                      openAmountPicker();
                    }
                  }}
                >
                  <span
                    className={`font-bold text-black dollor-mr mr-2 ${
                      isMultiLayout ? "text-lg" : "text-xl md:text-2xl"
                    } `}
                  >
                    $
                  </span>
                  <span
                    className={`amount-input-no-spinner flex-1 min-w-0 bg-transparent border-none p-0 font-bold text-black outline-none transition text-xl`}
                  >
                    {amountInput}
                  </span>
                  <MoreVertical
                    size={20}
                    className="text-black opacity-50 ml-auto shrink-0"
                  />
                </button>

                <div
                  className={`text-right font-semibold md:hidden justify-around flex flex-col whitespace-nowrap text-white/55 ${
                    isMultiLayout
                      ? "text-[10px] leading-[14px]"
                      : "text-[11px] leading-4 md:text-xs md:leading-5"
                  } `}
                >
                  <span>
                    Min $
                    {Number(wallet?.binary?.min_amount || 0).toFixed(2)}
                  </span>
                  <span>
                    Max $
                    {Number(wallet?.binary?.max_amount || 0).toFixed(2)}
                  </span>
                </div>

                {/* Quick amounts chips */}
                <div className="min-w-0">
                  <div
                    className={`grid shrink-0 grid-cols-2 ${
                      isMultiLayout
                        ? "basis-[40%] gap-1"
                        : "basis-[40%] gap-1.5 md:mt-2 md:flex md:w-full md:basis-auto md:gap-2"
                    } `}
                  >
                    {dynamicAmountOptions
                      .slice(0, 3)
                      .map((quickAmount) => (
                        <button
                          key={quickAmount}
                          type="button"
                          className={`${
                            isMultiLayout
                              ? "aspect-square h-6 min-w-0 w-full rounded-md px-1 text-[11px]"
                              : "h-6 min-w-0 rounded-md px-1 text-xs md:h-11 md:w-full md:text-sm"
                          } border font-bold transition hover:brightness-110 ${
                            amount === quickAmount
                              ? "border-[#fc4420] bg-[#fc4420] text-white"
                              : "border-white/20 bg-[#3a3f45] text-white/80 hover:border-white/25 hover:bg-[#454b52]"
                          } `}
                          onClick={() => {
                            if (typeof playSound === "function") playSound("button");
                            setAmountInput(quickAmount);
                          }}
                        >
                          ${Number(quickAmount).toFixed(2)}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Up & Down Buttons */}
      <div
        className={`grid ${
          isMultiLayout
            ? "mt-2 gap-2 sticky bottom-0 grid-cols-2"
            : "mt-1.5 gap-2 md:mt-2.5 md:gap-3 grid-cols-2 "
        } `}
      >
        <div className="relative group">
          <button
            type="button"
            className={`flex w-full items-center justify-center gap-3 up-new-button up-arcade-3d font-bold text-white transition-all duration-150 hover:brightness-110 active:scale-[0.96] active:brightness-90 active:shadow-inner disabled:cursor-not-allowed disabled:opacity-45 ${
              isMultiLayout
                ? "min-h-[50px] rounded-lg px-4 text-base"
                : "md:min-h-[50px] h-[50px] rounded-xl px-6 text-lg"
            } `}
            onClick={() => {
              setButtonShockwave("up");
              setTimeout(() => setButtonShockwave(null), 650);
              handlePlaceOrder("up");
            }}
            disabled={isTradeActionDisabled}
          >
            {buttonShockwave === "up" && <span className="shockwave-ring-up" />}
            <TrendingUp size={26} strokeWidth={3} />
            <span>Up</span>
          </button>
          {symbolActiveOrdersCount >= 3 && (
            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
              ⚠️ Only 3 active trades are allowed at a time.
            </div>
          )}
        </div>

        <div className="relative group">
          <button
            type="button"
            className={`flex w-full items-center justify-center gap-3 down-new-button down-arcade-3d font-bold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45 ${
              isMultiLayout
                ? "min-h-[50px] rounded-lg px-4 text-base"
                : "md:min-h-[50px] h-[50px] rounded-xl px-6 text-lg"
            } `}
            onClick={() => {
              setButtonShockwave("down");
              setTimeout(() => setButtonShockwave(null), 650);
              handlePlaceOrder("down");
            }}
            disabled={isTradeActionDisabled}
          >
            {buttonShockwave === "down" && <span className="shockwave-ring-down" />}
            <TrendingDown size={26} strokeWidth={3} />
            <span>Down</span>
          </button>
          {symbolActiveOrdersCount >= 3 && (
            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
              ⚠️ Only 3 active trades are allowed at a time.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

